import { Component, Vue, Watch } from "vue-property-decorator";

import * as firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import { v4 as uuidv4 } from "uuid";

import { FacilitySettingsModel } from "@/models/FacilitySettings";

import { Countries, countries } from "@/consts/countries";
import { CURRENCIES } from "@/consts/currencies";
import { Resident } from "@/models/resident/Resident";
import { ResidentAccessesOnMyhub } from "@/enums/ResidentAccessesOnMyhub";
import { dateFormats, timeFormats } from "@/consts/DateTimeFormats";
import { getDominantColor } from "@/utils/upload-image";
import { familyNewsletterOptions } from "@/consts/newsletter";
import { Languages, languages } from "@/consts/languages";
import { batchUpdate } from "@/utils/batchUpdate";
import { API } from "@/api";
import StaffDirectory from "@/views/settings/staffDirectory/StaffDirectory.vue";
import moment from "moment";
import {StaffAccessLevels} from "@/enums/StaffAccessLevels";
import ConfirmDlg from '@/components/popup/confirm-dialog/ConfirmDlg.vue';
import { TemperatureFormats } from '@/enums/TemperatureFormats';
import { UserTypeEnum } from '@/enums/UserTypeEnum';
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';
import { getRegionName } from "@/utils/functionsRegion";
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";
import { isAustraliaResHub } from "@/utils/projectConfig";
import { stopIdleSessionTimer, initIdleSessionTimer } from "@/bootstrap/initIdleSessionTimer";
import { stopActiveSessionTimer, initActiveSessionTimer } from "@/bootstrap/initActiveSessionTimer";
import { getStaffAccessLevels } from "@/utils/accessControl/getAccessLevelDisplayName";
import { FeatureFlags, isFeatureEnabled } from "@/consts/featureFlags";

@Component({
  components: {
    StaffDirectory,
    ConfirmDlg
  }
})
export default class FacilitySettings extends Vue {
  countries: Countries[] = countries;
  languages: Languages[] = languages;
  timeZones = moment.tz.names();
  currencies = CURRENCIES;
  currentDate: number = new Date().getDate();
  search = "";
  staffHubLogoUploadStatus = 100;
  facilityLogoUploadStatus = 100;
  orgLogoUploadStatus = 100;

  appName = "";
  packageName = "";
  mediaAppSelectedIndex = -1;
  tvAppName = "";
  arecheckedesdisabled = true;
  tvPackageName = "";
  tvAppStatus = false;
  tvAppSelectedIndex = -1;
  isMediaAppUpdating = false
  isMediaAppDeleting = false
  isTvAppUpdating = false
  dialog = false;
  settings = null
  residentAccessesOnMyhub = ResidentAccessesOnMyhub;
  residentFeaturesChanged = {
    isServicesChanged: false,
    isCommunityChanged: false,
    isMediaChanged: false,
    isMaintenanceChanged: false,
    isNoticeboardChanged: false,
    isBudgetChanged: false,
    isCalendarChanged: false
  }
  familyFeaturesChanged = {
    isCareSummaryChanged: false,
    isCarePlanChanged: false,
    isDailyCareChanged: false,
    isDailyPlanChanged: false,
    isEnableFamilyCareFeedbackChanged: false
  }
  dateFormats = dateFormats
  timeFormats = timeFormats
  loading = false;
  isLoadingModules = true;
  isChangedFacilityName = false;
  facility = new FacilitySettingsModel();
  facilityCopy = null
  organisation: any = { isGroveMenuFeatureEnabled: false }
  isInvalidFacilityLogo = false
  isInvalidOrgLogo = false
  isInvalidStaffHubLogo = false
  isFacilityNameChanged = false
  temperatureFormats = Object.values(TemperatureFormats)
  integrationApiDataLoading = false;
  integrationApiData = {
     genericApi: {
        apiKey: '',
        devApiKey: '',
     },
    squarePayment:{
      appId: '',
      paymentLocationId: '',
      accessToken: ''
    }
  };
  userSessionsInactiveTimeouts = [
    { name: 'No session time out', seconds: 0 },
    { name: '15 minutes', seconds: 900 },
    { name: '30 minutes', seconds: 1800 },
  ]

   userSessionsTimeouts = [
    { name: 'No session time out', seconds: 0 },
    { name: '24 hours', seconds: 86400 },
    { name: '48 hours', seconds: 172800 },
    { name: 'Weekly', seconds: 604800 }
  ]

  get selectedFacility(): FacilitySettingsModel {
    return this.$store.state.facilityModule.selectedFacility;
  }

  get initialAllowStaffFamilyThreads(): boolean {
    return this.selectedFacility?.allowStaffFamilyThreads === true;
  }

  get staffFamilyMessagingEnabled(): boolean {
    return isFeatureEnabled(this.facility, FeatureFlags.staffFamilyMessagingEnabled);
  }

  get residents(): Resident[] {
    return this.$store.state.residentModule.residentsShortData;
  }

  get familyMembers() {
    return this.$store.state.residentModule.familyMembers;
  }

  get isUserOrgAdmin() {
    return this.$store.state.authModule?.user?.accessLevel === StaffAccessLevels.OrgAdmin;
  }

  get currentUser() {
    return this.$store.state.authModule.user;
  }

  get canChangeSessionTimeouts(){
      return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.FamilyMembers_Details_ChangePoaStatus)
  }

  get isAustraliaResHub() {
    return isAustraliaResHub();
  }

  get staffAccessLevelsList() {
    const hasMaintenanceFeature = this.facility?.modules?.maintenance?.isEnabled;
    return getStaffAccessLevels(false, hasMaintenanceFeature).filter(
      (level) => level.value !== StaffAccessLevels.MaintenanceAdmin && level.value !== StaffAccessLevels.MaintenanceUser
    );
  }

  async created(): Promise<void> {
    // Ensure initial render with skeleton loader happens first
    await this.$nextTick();
    
    // Load facility data first (needed for Modules display)
    await this.$store.dispatch("getFacility");
    
    this.initWeeklyLifestyleEventsReportOption(this.facility.countryIso)
    // Create a shallow copy to avoid mutating the store directly
    this.facility = { ...this.selectedFacility }

    // Ensure default "No session time out" (0 seconds) if value is null/undefined
    if (this.facility.staffIdleSessionTimeoutSeconds == null) this.facility.staffIdleSessionTimeoutSeconds = 0;
    if (this.facility.residentIdleSessionTimeoutSeconds == null) this.facility.residentIdleSessionTimeoutSeconds = 0;
    if (this.facility.familyIdleSessionTimeoutSeconds == null) this.facility.familyIdleSessionTimeoutSeconds = 0;

    if (this.facility.staffActiveSessionTimeoutSeconds == null) this.facility.staffActiveSessionTimeoutSeconds = 0;
    if (this.facility.residentActiveSessionTimeoutSeconds == null) this.facility.residentActiveSessionTimeoutSeconds = 0;
    if (this.facility.familyActiveSessionTimeoutSeconds == null) this.facility.familyActiveSessionTimeoutSeconds = 0;

    // Initialize new property with default value if it doesn't exist
    if (this.facility.isEnableFamilyCareFeedback === undefined || this.facility.isEnableFamilyCareFeedback === null) {
      this.facility.isEnableFamilyCareFeedback = false;
    }
    if (!this.facility.familyMessagingAccessLevels) {
      this.facility.familyMessagingAccessLevels = [StaffAccessLevels.OrgAdmin, StaffAccessLevels.HomeAdmin];
    }
    // Use shallow copy for better performance
    this.facilityCopy = { ...this.facility }

    // Modules can now display - hide loading state
    this.isLoadingModules = false;
    
    // Load remaining data in background (not needed for initial render)
    Promise.all([
      this.loadIntegrationApiData(),
      API.organisations.getOrganisationById(this.facility.orgId)
    ]).then(([, organisation]) => {
      this.organisation = organisation;
    });
  }

  async loadIntegrationApiData() {
    this.integrationApiDataLoading = true;
    this.integrationApiData = await this.$store.dispatch('getIntegrationApiData');
    this.integrationApiDataLoading = false;
  }


  initWeeklyLifestyleEventsReportOption(countryIso){
  // for 'US' country the report should be enabled by default
  this.facility.isEnableWeeklyLifestyleEventsReport = 
  (this.facility.isEnableWeeklyLifestyleEventsReport == null && countryIso === 'US')? true : this.facility.isEnableWeeklyLifestyleEventsReport
  }

  async updateFacility(): Promise<void> {
    this.loading = true
    this.facility.facilityId = this.facility.id;
    this.facility.isEnableWeeklyLifestyleEventsReport = this.facility.isEnableWeeklyLifestyleEventsReport || false;
    this.facility.isEnableCSVImportForResidents = this.facility.isEnableCSVImportForResidents || false;
    this.facility.isEnableManuallyAddDeleteResidents = this.facility.isEnableManuallyAddDeleteResidents || false;
    this.facility.isEnableResidentsCanInviteFamily = this.facility.isEnableResidentsCanInviteFamily || false; 
    this.facility.isEnableADT = this.facility.isEnableADT || false;
    this.facility.featureFamilyEngagement = this.facility.featureFamilyEngagement || false;
    this.facility.isEnableFamilyCareFeedback = this.facility.isEnableFamilyCareFeedback || false;
    this.facility.allowStaffFamilyThreads = this.facility.allowStaffFamilyThreads || false;
    this.facility.familyCanInitiateContact = this.facility.familyCanInitiateContact ?? true;
    if (!this.facility.familyMessagingAccessLevels) {
      this.facility.familyMessagingAccessLevels = [StaffAccessLevels.OrgAdmin, StaffAccessLevels.HomeAdmin];
    }

    if(this.facility.isEnableADT === true){
      this.facility.isEnableCSVImportForResidents = false;
      this.facility.isEnableManuallyAddDeleteResidents = false;
    }
    
    this.setFamilyNewsletter();
    this.updateFacilityName();
    this.addAuditLogs();

    if(this.integrationApiData?.genericApi && this.integrationApiData?.squarePayment){
      const res = await this.$store.dispatch('updateIntegrationApiData', this.integrationApiData);
      if(res){
        await this.loadIntegrationApiData()
      }
      else{
        this.$toast.open({ message: 'Error updating Integration API data', type: "error" });
      }
    }

    await this.$store.dispatch("updateFacility", this.facility).then(async () => {
      if(this.isFacilityNameChanged) {
        await this.$store.dispatch("updateFacilityStaffMailingList", {
          facilityId: this.facility.id,
          facilityName: this.facility.name,
          updatedById: this.currentUser.id,
          updatedByType: UserTypeEnum.Staff,
          initiatorAccessLevel: this.currentUser.accessLevel,
          initiatorName: this.currentUser.name,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Facility}`],
        })
      }

      await this.updateUsersIdleSessionTimeouts()
      await this.updateUsersActiveSessionTimeouts()

      this.updateResidentsAccessesOnMyhub().then(() => {
        this.$toast.open({ message: (this.$i18n.messages[this.$i18n.locale]['Settings.FacilitySettingsUpdated'] as string), type: "success" });
      }).catch(() => {
        this.$toast.open({ message: (this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.UnhandledError'] as string), type: "error" });
      }).finally(() => {
        this.loading = false
      })
    })

    if (this.facility.languageIso != this.facilityCopy.languageIso) {
      await this.$store.dispatch("translate", { locale: this.facility.languageIso });
      this.facilityCopy.languageIso = this.facility.languageIso
    }
  }

  async updateUsersIdleSessionTimeouts(){
    if ((this.facility.staffIdleSessionTimeoutSeconds !== this.facilityCopy.staffIdleSessionTimeoutSeconds) && this.facility.staffIdleSessionTimeoutSeconds >= 0) {
      await firebase.app().functions(getRegionName()).httpsCallable('staff-updateIdleSessionTimeouts')({
        facilityId: this.facility.id,
        seconds: this.facility.staffIdleSessionTimeoutSeconds
      })
      this.facilityCopy.staffIdleSessionTimeoutSeconds = this.facility.staffIdleSessionTimeoutSeconds

      if(this.facility.staffIdleSessionTimeoutSeconds <= 0){
        stopIdleSessionTimer(); 
      }

      if(this.facility.staffIdleSessionTimeoutSeconds > 0) {
        stopIdleSessionTimer();
        initIdleSessionTimer(this.facility.staffIdleSessionTimeoutSeconds);
      }
    }

    if ((this.facility.residentIdleSessionTimeoutSeconds !== this.facilityCopy.residentIdleSessionTimeoutSeconds) && this.facility.residentIdleSessionTimeoutSeconds >= 0) {   
      await firebase.app().functions(getRegionName()).httpsCallable('resident-updateIdleSessionTimeouts')({
        facilityId: this.facility.id,
        seconds: this.facility.residentIdleSessionTimeoutSeconds
      })
      this.facilityCopy.residentIdleSessionTimeoutSeconds = this.facility.residentIdleSessionTimeoutSeconds
    }

    if ((this.facility.familyIdleSessionTimeoutSeconds !== this.facilityCopy.familyIdleSessionTimeoutSeconds) && this.facility.familyIdleSessionTimeoutSeconds >= 0) {
      await firebase.app().functions(getRegionName()).httpsCallable('familyUser-updateIdleSessionTimeouts')({
        facilityId: this.facility.id,
        seconds: this.facility.familyIdleSessionTimeoutSeconds
      })
      this.facilityCopy.familyIdleSessionTimeoutSeconds = this.facility.familyIdleSessionTimeoutSeconds
     }
    }

    async updateUsersActiveSessionTimeouts(){
      if ((this.facility.staffActiveSessionTimeoutSeconds !== this.facilityCopy.staffActiveSessionTimeoutSeconds) && this.facility.staffActiveSessionTimeoutSeconds >= 0) {
        await firebase.app().functions(getRegionName()).httpsCallable('staff-updateActiveSessionTimeouts')({
          facilityId: this.facility.id,
          seconds: this.facility.staffActiveSessionTimeoutSeconds
        });

        this.facilityCopy.staffActiveSessionTimeoutSeconds = this.facility.staffActiveSessionTimeoutSeconds;

        if(this.facility.staffActiveSessionTimeoutSeconds <= 0){
          stopActiveSessionTimer()
        }

        if(this.facility.staffActiveSessionTimeoutSeconds > 0) {
          stopActiveSessionTimer();
          initActiveSessionTimer(this.facility.staffActiveSessionTimeoutSeconds);
        }
      }

      if ((this.facility.residentActiveSessionTimeoutSeconds !== this.facilityCopy.residentActiveSessionTimeoutSeconds) && this.facility.residentActiveSessionTimeoutSeconds >= 0) {
        await firebase.app().functions(getRegionName()).httpsCallable('resident-updateActiveSessionTimeouts')({
          facilityId: this.facility.id,
          seconds: this.facility.residentActiveSessionTimeoutSeconds
        })
        this.facilityCopy.residentActiveSessionTimeoutSeconds = this.facility.residentActiveSessionTimeoutSeconds
      }

      if ((this.facility.familyActiveSessionTimeoutSeconds !== this.facilityCopy.familyActiveSessionTimeoutSeconds) && this.facility.familyActiveSessionTimeoutSeconds >= 0) {
        await firebase.app().functions(getRegionName()).httpsCallable('familyUser-updateActiveSessionTimeouts')({
          facilityId: this.facility.id,
          seconds: this.facility.familyActiveSessionTimeoutSeconds
        })
        this.facilityCopy.familyActiveSessionTimeoutSeconds = this.facility.familyActiveSessionTimeoutSeconds
      }
    }


  updateFacilityName() {
    this.loading = true
    const facility = this.$store.state.authModule.user?.facilities?.find(x => x.id == this.facility.id);

    if (facility && this.isChangedFacilityName) {
      facility.name = this.facility.name
      this.$store.commit("setCurrentFacilityData", this.facility);
      this.$store.dispatch("updateFacilityName", {
        name: this.facility.name,
        facilityId: this.facility.id,
        facilityOrgId: this.facility.orgId,
      }).then(() => {
        this.loading = false
      })
    }
  }

  setFamilyNewsletter() {
    if (!this.facility.familyNewsletterOptions && this.facility.isEnableFamilyNewsletter) {
      this.facility.familyNewsletterOptions = familyNewsletterOptions
    }
  }

  saveStaffHubLogo(event: any) {

    if(!event.target.files[0]) return

    this.isInvalidStaffHubLogo = !['png', 'jpeg', 'jpg'].includes(this.getExtension(event.target.files[0].name).toLowerCase())
    
    if(this.isInvalidStaffHubLogo) return

    const ref = uuidv4();
    const storageRef = firebase
      .storage()
      .ref()
      .child(this.facility.id)
      .child("facility")
      .child("staffHubLogo")
      .child(ref)
      .put(event.target.files[0]);

    storageRef.on(
      `state_changed`,
      snapshot => {
        this.staffHubLogoUploadStatus =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      },
      error => {
        console.log(error.message);
      },
      () => {
        storageRef.snapshot.ref.getDownloadURL().then(async url => {
          console.log(url);
          this.facility.staffHubLogoUrl = url;
          this.facility.staffHubLogoColor = await getDominantColor(url)
          this.updateFacility();
        });
      }
    );
  }

  saveOrgLogo(event: any) {

    if(!event.target.files[0]) return

    this.isInvalidOrgLogo = !['png', 'jpeg', 'jpg'].includes(this.getExtension(event.target.files[0].name).toLowerCase())

    if(this.isInvalidOrgLogo) return

    const ref = uuidv4();
    const storageRef = firebase
      .storage()
      .ref()
      .child(this.facility.id)
      .child("facility")
      .child("orgLogo")
      .child(ref)
      .put(event.target.files[0]);

    storageRef.on(
      `state_changed`,
      snapshot => {
        this.orgLogoUploadStatus =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      },
      error => {
        console.log(error.message);
      },
      () => {
        storageRef.snapshot.ref.getDownloadURL().then(async url => {
          console.log(url);
          this.facility.orgLogoUrl = url;
          this.facility.orgLogoColor = await getDominantColor(url)
          this.updateFacility();
        });
      }
    );
  }

  saveFacilityLogo(event: any) {

    if(!event.target.files[0]) return

    this.isInvalidFacilityLogo = !['png', 'jpeg', 'jpg'].includes(this.getExtension(event.target.files[0].name).toLowerCase())

    if(this.isInvalidFacilityLogo) return

    const ref = uuidv4();
    const storageRef = firebase
      .storage()
      .ref()
      .child(this.facility.id)
      .child("facility")
      .child("facilityLogo")
      .child(ref)
      .put(event.target.files[0]);

    storageRef.on(
      `state_changed`,
      snapshot => {
        this.facilityLogoUploadStatus =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      },
      error => {
        console.log(error.message);
      },
      () => {
        storageRef.snapshot.ref.getDownloadURL().then(async url => {
          console.log(url);
          this.facility.facilityLogoUrl = url;
          this.facility.facilityLogoColor = await getDominantColor(url)
          this.updateFacility();
        });
      }
    );
  }

  getExtension(filename) {

    const parts = filename.split('.')
    const ext = parts[parts.length - 1]

    return ext
  }

  addTile(): void {
    if (this.facility.skinSettings.tiles == null)
      this.facility.skinSettings.tiles = [];

    this.facility.skinSettings.tiles.push({
      appName: "",
      packageName: "",
      icon: "",
      text: "",
      id: uuidv4(),
      type: "app"
    });
  }

  removeTile(tile): void {
    const index = this.facility.skinSettings.tiles.indexOf(tile);
    if (index >= 0) this.facility.skinSettings.tiles.splice(index, 1);
  }

  getSkinImage(skin): string {
    if (skin == "simplex")
      return require("../../../../assets/skins/simplexskin.png");
    if (skin == "default")
      return require("../../../../assets/skins/defaultskin.png");
  }

  async addMediaApp() {
    await firebase
      .firestore()
      .collection("facility")
      .doc(this.facility.id)
      .update({
        mediaApps: firebase.firestore.FieldValue.arrayUnion({
          id: uuidv4(),
          appName: this.appName,
          packageName: this.packageName,
          status: true
        }),
        updatedById: this.$store.state.authModule.user.id,
        updatedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Facility}`],
        initiatorAccessLevel: this.currentUser.accessLevel,
        initiatorName: this.currentUser.name
      })
      .then(async () => {
        this.packageName = "";
        this.appName = "";
        const facility = await this.$store.dispatch("getFacility");
        this.updateMediaAppsToResident(facility.mediaApps);
      })
      .catch(err => {
        console.error(err);
      });
  }

  async addTvApp() {
    await firebase
      .firestore()
      .collection("facility")
      .doc(this.facility.id)
      .update({
        tvApps: firebase.firestore.FieldValue.arrayUnion({
          id: uuidv4(),
          tvAppName: this.tvAppName,
          tvPackageName: this.tvPackageName,
          status: this.tvAppStatus
        }),
        updatedById: this.$store.state.authModule.user.id,
        updatedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Facility}`],
        initiatorAccessLevel: this.currentUser.accessLevel,
        initiatorName: this.currentUser.name
      })
      .then(async () => {
        this.tvAppName = "";
        this.tvPackageName = "";
        this.tvAppStatus = false;
        await this.$store.dispatch("getFacility");
      })
      .catch(err => {
        console.error(err);
      });
  }

  async updateMediaApp(mediaApp) {

    this.isMediaAppUpdating = true

    let facilityMediaApps = this.facility.mediaApps || []

    facilityMediaApps = facilityMediaApps.filter(x => x.id !== mediaApp.id)

    facilityMediaApps.push(mediaApp)

    if (mediaApp) {
      await firebase
        .firestore()
        .collection("facility")
        .doc(this.facility.id)
        .update({
          mediaApps: facilityMediaApps
        })
        .then(async () => {
          this.packageName = "";
          this.appName = "";
          const facility = await this.$store.dispatch("getFacility");
          await this.updateMediaAppsToResident(facility.mediaApps);
          this.isMediaAppUpdating = false
          this.mediaAppSelectedIndex = -1
        })
        .catch(err => {
          this.isMediaAppUpdating = false
          this.mediaAppSelectedIndex = -1
          console.error(err);
        })
    }
  }

  async updateTvApp(tvApp) {

    let facilityTvApps = this.facility.tvApps || []

    facilityTvApps = facilityTvApps.filter(x => x.id !== tvApp.id)

    facilityTvApps.push(tvApp)

    if (tvApp) {
      await firebase
        .firestore()
        .collection("facility")
        .doc(this.facility.id)
        .update({
          tvApps: facilityTvApps,
          updatedById: this.$store.state.authModule.user.id,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Facility}`],
          initiatorAccessLevel: this.currentUser.accessLevel,
          initiatorName: this.currentUser.name
        })
        .then(async () => {
          this.tvAppSelectedIndex = -1
          this.tvPackageName = "";
          this.tvAppName = "";
          await this.$store.dispatch("getFacility");
        })
        .catch(err => {
          console.error(err);
        });
    }
  }

  async deleteTvApp(tvApp) {

    if (tvApp) {
      await firebase
        .firestore()
        .collection("facility")
        .doc(this.facility.id)
        .update({
          tvApps: firebase.firestore.FieldValue.arrayRemove(tvApp),
          updatedById: this.$store.state.authModule.user.id,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Facility}`],
          initiatorAccessLevel: this.currentUser.accessLevel,
          initiatorName: this.currentUser.name
        })
        .then(async () => {
          this.tvPackageName = "";
          this.tvAppName = "";
          await this.$store.dispatch("getFacility");
        })
        .catch(err => {
          console.error(err);
        });
    }
  }

  async deleteMediaApp(mediaApp) {
    if (mediaApp) {
      this.isMediaAppDeleting = true
      await firebase
        .firestore()
        .collection("facility")
        .doc(this.facility.id)
        .update({
          mediaApps: firebase.firestore.FieldValue.arrayRemove(mediaApp),
          updatedById: this.$store.state.authModule.user.id,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Facility}`],
          initiatorAccessLevel: this.currentUser.accessLevel,
          initiatorName: this.currentUser.name
        })
        .then(async () => {
          this.packageName = "";
          this.appName = "";
          const facility = await this.$store.dispatch("getFacility");
          await this.updateMediaAppsToResident(facility.mediaApps);
          this.isMediaAppDeleting = false
          this.mediaAppSelectedIndex = - 1
        })
        .catch(err => {
          this.isMediaAppDeleting = false
          this.mediaAppSelectedIndex = - 1
          console.error(err);
        });
    }
  }

  async updateMediaAppsToResident(mediaApps) {
    const itemsToUpdate = []
    this.residents.forEach((doc) => {

      const updates = { mediaAppOverrides: [] }

      if (!doc.mediaAppOverrides) {
        updates.mediaAppOverrides = this.facility.mediaApps;
      }

      mediaApps.forEach(media => {
        if (!updates.mediaAppOverrides.find(mediaResident => mediaResident.id === media.id)) {
          updates.mediaAppOverrides.push(media);
        }
      });

      updates.mediaAppOverrides.forEach(mediaResident => {
        if (!mediaApps.find(media => media.id === mediaResident.id)) {
          const residentMediasResult = updates.mediaAppOverrides.filter(
            item => item.id != mediaResident.id
          );
          updates.mediaAppOverrides = residentMediasResult;
        }
      });

      itemsToUpdate.push({ docId: doc.id, updates: updates })
    });


    return await batchUpdate('residents', itemsToUpdate)
  }

  async updateResidentsAccessesOnMyhub() {
    if (this.residentFeaturesChanged.isServicesChanged
      || this.residentFeaturesChanged.isCommunityChanged
      || this.residentFeaturesChanged.isNoticeboardChanged) {

      const itemsToUpdate: any[] = []
      this.residents.forEach(resident => {
        if (resident?.accessesOnMyhub) {
          resident?.accessesOnMyhub.forEach(x => {
            if (x.name === this.residentAccessesOnMyhub.PremiumServices || x.name === this.residentAccessesOnMyhub.LifestyleEvents) {
              x.isEnabled = this.facility.additionalServicesEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Community) {
              x.isEnabled = this.facility.featureLifestyle;
            }
            if (x.name === this.residentAccessesOnMyhub.Media) {
              x.isEnabled = this.facility.mediaEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Maintenance) {
              x.isEnabled = this.facility.maintenanceEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Noticeboard) {
              x.isEnabled = this.facility.noticeboardEnabled;
            }
            if (x.name === this.residentAccessesOnMyhub.Calendar) {
              x.isEnabled = this.facility.featureLifestyle;
            }
          })
          itemsToUpdate.push({ docId: resident.id, updates: { accessesOnMyhub: resident?.accessesOnMyhub } })
        }
      })

      await batchUpdate('residents', itemsToUpdate).then(() => {
        console.log(`updated (${itemsToUpdate.length})/${this.residents.length} residents`)
      });
    }
  }

  onIsEnableADTChange(value) {
    if(value === true){
      this.facility.isEnableCSVImportForResidents = false;
      this.facility.isEnableManuallyAddDeleteResidents = false;
    }
  }

  addAuditLogs() {
    this.facility.updatedById = this.$store.state.authModule.user.id;
    this.facility.updatedByType = UserTypeEnum.Staff;
    this.facility.typesOfRecordChanged = [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Facility}`];
    this.facility.initiatorAccessLevel = this.currentUser.accessLevel,
    this.facility.initiatorName = this.currentUser.name
  }

  @Watch("facility.name")
  onFacilityName() {
    this.isChangedFacilityName = true;
  }

  @Watch("facility.additionalServicesEnabled")
  onChangeAdditionalServicesEnabled() {
    this.residentFeaturesChanged.isServicesChanged = true;
  }

  @Watch("facility.mediaEnabled")
  onChangeMediaEnabled() {
    this.residentFeaturesChanged.isMediaChanged = true;
  }

  @Watch("facility.maintenanceEnabled")
  onChangeMaintenanceEnabled() {
    this.residentFeaturesChanged.isMaintenanceChanged = true;
  }

  @Watch("facility.noticeboardEnabled")
  onChangeNoticeEnabled() {
    this.residentFeaturesChanged.isNoticeboardChanged = true;
  }

  @Watch("facility.featureLifestyle")
  onChangeFeatureLifestyle() {
    this.residentFeaturesChanged.isCalendarChanged = true;
    this.residentFeaturesChanged.isCommunityChanged = true;
  }

  @Watch("facility.isCareSummary")
  onChangeCareSummary(value) {
    this.familyFeaturesChanged.isCareSummaryChanged = true;
  }

  @Watch("facility.isDailyPlan")
  onChangeDailyPlan(value) {
    this.familyFeaturesChanged.isDailyPlanChanged = true;
  }

  @Watch("facility.isCarePlan")
  onChangeCarePlan(value) {
    this.familyFeaturesChanged.isCarePlanChanged = true;
    this.facility.isEnabledCarePlan = value;
  }

  @Watch("facility.isDailyCare")
  onChangeDailyCare(value) {
    this.familyFeaturesChanged.isDailyCareChanged = true;
  }

  @Watch("facility.isEnableFamilyCareFeedback")
  onChangeEnableFamilyCareFeedback(value) {
    this.familyFeaturesChanged.isEnableFamilyCareFeedbackChanged = true;
  }

  @Watch("facility.name")
  onChangeFacilityName(newValue, oldValue) {
    if(oldValue && newValue !== oldValue) {
      this.isFacilityNameChanged = true;
    }
  }

  @Watch("facility.countryIso")
  onChangeCountry(value, oldValue) {
    if(value && oldValue) {
      this.facility.temperatureFormat = value === "US" ? TemperatureFormats.Fahrenheit : TemperatureFormats.Celsius;
    }
  }
}